import type { ServiceRequest } from '@medplum/fhirtypes';
import { HEALTHIE_LAB_TEST_CODE_SYSTEM, HEALTHIE_SYSTEM_URL } from '@bariendo/lib/constants';

function uniqueDefinedDisplays(displays: (string | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const d of displays) {
    if (d && !seen.has(d)) {
      seen.add(d);
      out.push(d);
    }
  }
  return out;
}

/**
 * Healthie migrations use a single ServiceRequest (no basedOn) with multiple
 * `code.coding` entries—unlike Health Gorilla orders, which use child ServiceRequests
 * with basedOn pointing at a parent panel.
 */
export function isHealthieLabPanelServiceRequest(sr: ServiceRequest): boolean {
  if (sr.basedOn?.length) {
    return false;
  }
  const hasHealthieIdentifier = sr.identifier?.some((id) => id.system === HEALTHIE_SYSTEM_URL);
  const hasHealthieTestCoding = sr.code?.coding?.some((c) => c.system === HEALTHIE_LAB_TEST_CODE_SYSTEM);
  return Boolean(hasHealthieIdentifier || hasHealthieTestCoding);
}

/**
 * Resolves human-readable test names for the lab order card: child SRs (HG-style)
 * or Healthie multi-coding panel on a single SR.
 */
export function getLabOrderSelectedTestDisplays(serviceRequests: ServiceRequest[]): string[] {
  const childRequests = serviceRequests.filter((sr) => Boolean(sr.basedOn?.length));
  if (childRequests.length > 0) {
    return uniqueDefinedDisplays(childRequests.map((sr) => sr.code?.coding?.[0]?.display));
  }

  const mainRequest = serviceRequests.find((sr) => !sr.basedOn?.length) ?? serviceRequests[0];
  if (mainRequest && isHealthieLabPanelServiceRequest(mainRequest)) {
    return uniqueDefinedDisplays(mainRequest.code?.coding?.map((c) => c.display) ?? []);
  }

  return [];
}
